import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "wouter";
import {
  ArrowLeft,
  Building2,
  Check,
  Eye,
  Layers,
  X,
} from "lucide-react";
import { fileUrl, formatArea, formatPriceHuf } from "@/lib/format";
import { unitDisplayPriceHuf } from "@shared/pricing";
import { RequestQuoteLink, unitQuoteHref } from "@/components/request-quote";
import {
  STATUS_FILL as APT_STATUS_FILL,
  STATUS_LABEL as APT_STATUS_LABEL,
  STATUS_STROKE as APT_STATUS_STROKE,
  statusLabel as aptStatusLabel,
  svgPoints,
} from "@/lib/apartment-status";
import { UNIT_STATUS_LABEL, unitSlug } from "@/lib/unit-helpers";
import type { Apartment, Floor, PolygonPoint, ProjectUnit } from "@shared/schema";

type FloorWithApartments = Floor & { apartments: Apartment[] };

const MAX_COMPARE = 4;

// Unit-status colours for the synthetic (no uploaded plan) fallback view.
const UNIT_STATUS_FILL: Record<ProjectUnit["status"], string> = {
  available: "#cfe7d4",
  reserved: "#fef3c7",
  sold: "#e5e5e5",
};
const UNIT_STATUS_STROKE: Record<ProjectUnit["status"], string> = {
  available: "#2f7a3e",
  reserved: "#b45309",
  sold: "#94a3b8",
};

/**
 * Resolve the detailed project unit a floor-plan apartment zone points to.
 * Prefers the explicit `unitId` link; falls back to matching the zone name
 * against a unit label so existing data works before an admin links zones.
 */
function resolveUnit(
  apt: Apartment,
  units: ProjectUnit[],
  unitById: Map<string, ProjectUnit>,
): ProjectUnit | null {
  if (apt.unitId) {
    const u = unitById.get(apt.unitId);
    if (u) return u;
  }
  const name = apt.name.trim().toLowerCase();
  if (!name) return null;
  return units.find((u) => u.label.trim().toLowerCase() === name) ?? null;
}

/**
 * Unified "Épület és alaprajz" section. Replaces the former two separate
 * sections (uploaded floor-plans + generated building explorer):
 *
 *   1. pick a floor (building cross-section)
 *   2. its floor-plan appears with clickable apartment zones
 *   3. clicking a zone shows the apartment details
 *   4. from there: open the unit's own page, or add it to a compare set
 *      that can be launched on the existing compare page.
 *
 * When the project has uploaded floor-plans (floors + apartment polygons) the
 * rich image view is used; otherwise it falls back to a generated view built
 * from the project units so every project still gets one building section.
 */
export function BuildingSection({
  floors,
  units,
  projectSlug,
  pricesHidden = false,
}: {
  floors: FloorWithApartments[];
  units: ProjectUnit[];
  projectSlug: string;
  /** Projekt-szintű ár-elrejtés → az ár helyén „Kérjen ajánlatot". */
  pricesHidden?: boolean;
}) {
  const [, navigate] = useLocation();
  const usableFloors = useMemo(
    () => floors.filter((f) => f.floorPlanImageKey),
    [floors],
  );
  const hasRealPlans = usableFloors.length > 0;
  const unitById = useMemo(
    () => new Map(units.map((u) => [u.id, u] as const)),
    [units],
  );

  // Selected units (by id) for comparison.
  const [compareIds, setCompareIds] = useState<string[]>([]);

  function toggleCompare(unitId: string) {
    setCompareIds((cur) => {
      if (cur.includes(unitId)) return cur.filter((id) => id !== unitId);
      if (cur.length >= MAX_COMPARE) return cur;
      return [...cur, unitId];
    });
  }

  function startCompare() {
    if (compareIds.length < 2) return;
    navigate(
      `/projektek/${projectSlug}/osszehasonlitas?units=${compareIds.join(",")}`,
    );
  }

  if (!hasRealPlans && units.length === 0) return null;

  const compareFull = compareIds.length >= MAX_COMPARE;
  const compareUnits = compareIds
    .map((id) => unitById.get(id))
    .filter((u): u is ProjectUnit => Boolean(u));

  return (
    <section className="bg-white border border-brand-100 max-w-full overflow-hidden">
      <div className="flex items-start gap-3 p-6 lg:p-8 border-b border-brand-100">
        <div className="w-12 h-12 grid place-items-center bg-accent text-white shrink-0">
          <Building2 size={22} />
        </div>
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-semibold uppercase tracking-wider text-brand-700">
            Épület és alaprajz
          </h2>
          <p className="text-xs text-brand-500 mt-0.5">
            Válasszon emeletet, majd egy lakásra kattintva megnézheti az
            adatait, megnyithatja a lakás oldalát vagy összehasonlításhoz
            adhatja.
          </p>
        </div>
      </div>

      {hasRealPlans ? (
        <RealPlanExplorer
          floors={usableFloors}
          units={units}
          unitById={unitById}
          projectSlug={projectSlug}
          pricesHidden={pricesHidden}
          compareIds={compareIds}
          compareFull={compareFull}
          onToggleCompare={toggleCompare}
        />
      ) : (
        <SyntheticExplorer
          units={units}
          projectSlug={projectSlug}
          pricesHidden={pricesHidden}
          compareIds={compareIds}
          compareFull={compareFull}
          onToggleCompare={toggleCompare}
        />
      )}

      <CompareBar
        units={compareUnits}
        onRemove={toggleCompare}
        onClear={() => setCompareIds([])}
        onStart={startCompare}
      />
    </section>
  );
}

// ─── Rich mode: uploaded floor-plans with apartment polygons ────────────────

function RealPlanExplorer({
  floors,
  units,
  unitById,
  projectSlug,
  pricesHidden,
  compareIds,
  compareFull,
  onToggleCompare,
}: {
  floors: FloorWithApartments[];
  units: ProjectUnit[];
  unitById: Map<string, ProjectUnit>;
  projectSlug: string;
  pricesHidden?: boolean;
  compareIds: string[];
  compareFull: boolean;
  onToggleCompare: (unitId: string) => void;
}) {
  const [activeFloorId, setActiveFloorId] = useState<string>(floors[0].id);
  const [hoverAptId, setHoverAptId] = useState<string | null>(null);
  const [selectedAptId, setSelectedAptId] = useState<string | null>(null);

  const active = floors.find((f) => f.id === activeFloorId) ?? floors[0];
  const selectedApt =
    active.apartments.find((a) => a.id === selectedAptId) ?? null;
  const selectedUnit = selectedApt
    ? resolveUnit(selectedApt, units, unitById)
    : null;

  return (
    <div className="p-6 lg:p-8 grid lg:grid-cols-3 gap-6 items-start">
      {/* Floor cross-section selector */}
      <aside className="lg:col-span-1 space-y-5 min-w-0">
        <div>
          <div className="text-[11px] uppercase tracking-widish font-semibold text-brand-400 mb-2">
            Emeletek
          </div>
          <BuildingStack
            floors={floors}
            activeId={active.id}
            onSelect={(id) => {
              setActiveFloorId(id);
              setSelectedAptId(null);
              setHoverAptId(null);
            }}
          />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widish font-semibold text-brand-400 mb-2">
            Státusz jelmagyarázat
          </div>
          <ul className="space-y-1.5 text-xs">
            {Object.keys(APT_STATUS_LABEL).map((s) => (
              <li key={s} className="flex items-center gap-2 text-brand-700">
                <span
                  className="w-4 h-4 rounded-sm border"
                  style={{
                    backgroundColor: APT_STATUS_FILL[s],
                    borderColor: APT_STATUS_STROKE[s],
                  }}
                />
                {APT_STATUS_LABEL[s]}
              </li>
            ))}
          </ul>
        </div>
      </aside>

      {/* Floor plan + details */}
      <div className="lg:col-span-2 min-w-0 max-w-full space-y-5">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widish text-brand-600 mb-3">
            {active.name} — alaprajz
          </h3>
          <div className="relative inline-block max-w-full">
            <img
              src={fileUrl(active.floorPlanImageKey!)}
              alt={`${active.name} alaprajz`}
              className="block max-w-full h-auto rounded border border-brand-200"
            />
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="absolute inset-0 w-full h-full"
            >
              {active.apartments.map((a) => {
                const pts = (a.polygonPoints ?? []) as PolygonPoint[];
                if (pts.length < 3) return null;
                const isActive = a.id === hoverAptId || a.id === selectedAptId;
                return (
                  <polygon
                    key={a.id}
                    points={svgPoints(pts)}
                    fill={APT_STATUS_FILL[a.status] ?? "rgba(0,0,0,0.1)"}
                    stroke={APT_STATUS_STROKE[a.status] ?? "#475569"}
                    strokeWidth={isActive ? 3 : 1.5}
                    vectorEffect="non-scaling-stroke"
                    className="cursor-pointer transition-opacity"
                    style={{
                      opacity: isActive ? 1 : 0.8,
                      touchAction: "manipulation",
                    }}
                    role="button"
                    aria-label={`${a.name} lakás — ${aptStatusLabel(a.status)}`}
                    onMouseEnter={() => setHoverAptId(a.id)}
                    onMouseLeave={() =>
                      setHoverAptId((cur) => (cur === a.id ? null : cur))
                    }
                    onClick={() => setSelectedAptId(a.id)}
                  />
                );
              })}
            </svg>
          </div>
        </div>

        {selectedApt ? (
          <ApartmentDetailPanel
            title={selectedApt.name}
            floorName={active.name}
            statusText={aptStatusLabel(selectedApt.status)}
            statusFill={APT_STATUS_FILL[selectedApt.status] ?? "#e5e5e5"}
            statusStroke={APT_STATUS_STROKE[selectedApt.status] ?? "#475569"}
            priceHuf={selectedApt.priceHuf}
            pricesHidden={pricesHidden}
            area={selectedApt.sizeSqm}
            rooms={selectedApt.rooms}
            unit={selectedUnit}
            projectSlug={projectSlug}
            isCompared={selectedUnit ? compareIds.includes(selectedUnit.id) : false}
            compareFull={compareFull}
            onToggleCompare={onToggleCompare}
          />
        ) : (
          <p className="rounded border border-dashed border-brand-200 bg-brand-50/50 px-4 py-6 text-center text-sm text-brand-500">
            Kattintson egy lakásra az alaprajzon a részletekért.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Vertical building cross-section: roof + one clickable band per floor.
 * Floors with an uploaded cover image ("Borítókép" in the admin EMELETEK
 * section) show it as a thumbnail band with the name overlaid; floors without
 * one keep the plain text band (F-MANUAL-DRSQ59BW).
 */
function BuildingStack({
  floors,
  activeId,
  onSelect,
}: {
  floors: FloorWithApartments[];
  activeId: string;
  onSelect: (id: string) => void;
}) {
  // Uploaded floors come ground→top; show top floor first like a real building.
  const topFirst = [...floors].reverse();
  // Ha van feltöltött emelet-borítókép, CSAK a képek jelennek meg — a rajzolt
  // ház-elemek (tető, lábazat, szürke keret) nélkül (F-MANUAL-DRSQ59BW
  // tesztelői kérés). A régi ház-ábra csak kép nélküli emeleteknél marad.
  const hasCovers = floors.some((f) => f.coverImageKey);
  return (
    <div className="max-w-[16rem]">
      {/* Roof — csak a rajzolt ház-nézetben */}
      {!hasCovers && (
        <div
          className="mx-auto"
          style={{
            width: 0,
            height: 0,
            borderLeft: "5.5rem solid transparent",
            borderRight: "5.5rem solid transparent",
            borderBottom: "1.75rem solid #5a5a5a",
          }}
          aria-hidden="true"
        />
      )}
      <div
        className={
          hasCovers
            ? "space-y-1.5"
            : "border-x-2 border-b-2 border-[#3d3d3d] bg-[#fafafa]"
        }
        role="tablist"
        aria-label="Emelet választó"
      >
        {topFirst.map((f) => {
          const isActive = f.id === activeId;
          const available = f.apartments.filter(
            (a) => a.status === "available",
          ).length;
          const meta = `${f.apartments.length} lakás${
            available > 0 ? ` · ${available} szabad` : ""
          }`;

          if (f.coverImageKey) {
            return (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onSelect(f.id)}
                className={`relative block w-full overflow-hidden rounded-md text-left transition-all ${
                  isActive ? "ring-2 ring-inset ring-accent" : ""
                }`}
              >
                <img
                  src={fileUrl(f.coverImageKey)}
                  alt={`${f.name} borítókép`}
                  loading="lazy"
                  draggable={false}
                  className={`block h-20 w-full object-cover transition-opacity ${
                    isActive ? "" : "opacity-90 hover:opacity-100"
                  }`}
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent px-3 pb-1.5 pt-6">
                  <div className="flex items-end justify-between gap-2">
                    <span className="text-sm font-semibold text-white drop-shadow">
                      {f.name}
                    </span>
                    <span className="shrink-0 text-[11px] text-white/85">
                      {meta}
                    </span>
                  </div>
                </div>
                {isActive && (
                  <span
                    className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-accent text-white shadow"
                    aria-hidden="true"
                  >
                    <Check size={12} />
                  </span>
                )}
              </button>
            );
          }

          return (
            <button
              key={f.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(f.id)}
              className={`w-full text-left px-3 py-3 transition-colors ${
                hasCovers
                  ? "rounded-md border border-brand-200 bg-white"
                  : "border-t-2 border-[#3d3d3d] first:border-t-0"
              } ${
                isActive
                  ? "bg-accent/15 ring-2 ring-inset ring-accent"
                  : "hover:bg-white"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`text-sm font-semibold ${
                    isActive ? "text-accent" : "text-brand-800"
                  }`}
                >
                  {f.name}
                </span>
                <span className="text-[11px] text-brand-500 shrink-0">
                  {meta}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      {/* Lábazat — csak a rajzolt ház-nézetben */}
      {!hasCovers && <div className="h-1 bg-[#3d3d3d]" aria-hidden="true" />}
    </div>
  );
}

// ─── Fallback mode: generated view from project units ───────────────────────

function SyntheticExplorer({
  units,
  projectSlug,
  pricesHidden,
  compareIds,
  compareFull,
  onToggleCompare,
}: {
  units: ProjectUnit[];
  projectSlug: string;
  pricesHidden?: boolean;
  compareIds: string[];
  compareFull: boolean;
  onToggleCompare: (unitId: string) => void;
}) {
  const floors = useMemo(() => {
    const byFloor = new Map<number, ProjectUnit[]>();
    for (const u of units) {
      const f = u.floor ?? 0;
      const arr = byFloor.get(f) ?? [];
      arr.push(u);
      byFloor.set(f, arr);
    }
    return Array.from(byFloor.entries())
      .map(([floor, list]) => ({
        floor,
        units: [...list].sort(
          (a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label),
        ),
      }))
      .sort((a, b) => b.floor - a.floor);
  }, [units]);

  const [view, setView] = useState<"building" | number>("building");
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);

  if (floors.length === 0) return null;

  const floorLabel = (f: number) => (f === 0 ? "Földszint" : `${f}. emelet`);
  const activeFloor =
    typeof view === "number" ? floors.find((f) => f.floor === view) : null;
  const selectedUnit =
    activeFloor?.units.find((u) => u.id === selectedUnitId) ?? null;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {view !== "building" && (
        <button
          type="button"
          onClick={() => {
            setView("building");
            setSelectedUnitId(null);
          }}
          className="inline-flex items-center gap-1 text-xs uppercase tracking-widish font-semibold text-brand-500 hover:text-accent transition-colors"
        >
          <ArrowLeft size={14} />
          Vissza az épülethez
        </button>
      )}

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        {view === "building" ? (
          <SyntheticBuildingView
            floors={floors}
            floorLabel={floorLabel}
            onPickFloor={(f) => {
              setView(f);
              setSelectedUnitId(null);
            }}
          />
        ) : (
          <div className="lg:col-span-2 min-w-0 max-w-full space-y-5">
            <SyntheticFloorPlan
              units={activeFloor?.units ?? []}
              selectedId={selectedUnitId}
              onSelect={setSelectedUnitId}
              pricesHidden={pricesHidden}
            />
            {selectedUnit ? (
              <ApartmentDetailPanel
                title={selectedUnit.label}
                floorName={
                  selectedUnit.floor != null
                    ? floorLabel(selectedUnit.floor)
                    : undefined
                }
                statusText={UNIT_STATUS_LABEL[selectedUnit.status]}
                statusFill={UNIT_STATUS_FILL[selectedUnit.status]}
                statusStroke={UNIT_STATUS_STROKE[selectedUnit.status]}
                priceHuf={unitDisplayPriceHuf(selectedUnit)}
                pricesHidden={pricesHidden}
                area={selectedUnit.areaSqm as unknown as number | null}
                rooms={selectedUnit.rooms}
                unit={selectedUnit}
                projectSlug={projectSlug}
                isCompared={compareIds.includes(selectedUnit.id)}
                compareFull={compareFull}
                onToggleCompare={onToggleCompare}
              />
            ) : (
              <p className="rounded border border-dashed border-brand-200 bg-brand-50/50 px-4 py-6 text-center text-sm text-brand-500">
                Kattintson egy lakásra a részletekért.
              </p>
            )}
          </div>
        )}

        {/* Legend + floor list */}
        <aside className="lg:col-span-1 space-y-4 lg:order-last">
          <div>
            <div className="text-[11px] uppercase tracking-widish font-semibold text-brand-400 mb-2">
              Státusz jelmagyarázat
            </div>
            <ul className="space-y-1.5 text-xs">
              {(["available", "reserved", "sold"] as const).map((s) => (
                <li key={s} className="flex items-center gap-2 text-brand-700">
                  <span
                    className="w-4 h-4 border"
                    style={{
                      background: UNIT_STATUS_FILL[s],
                      borderColor: UNIT_STATUS_STROKE[s],
                    }}
                  />
                  {UNIT_STATUS_LABEL[s]}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widish font-semibold text-brand-400 mb-2">
              Emeletek
            </div>
            <ul className="space-y-1">
              {floors.map((f) => (
                <li key={f.floor}>
                  <button
                    type="button"
                    onClick={() => {
                      setView(f.floor);
                      setSelectedUnitId(null);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm font-medium transition-colors flex items-center justify-between gap-2 ${
                      view === f.floor
                        ? "bg-accent/10 text-accent"
                        : "text-brand-700 hover:bg-brand-50"
                    }`}
                  >
                    <span>{floorLabel(f.floor)}</span>
                    <span className="text-xs text-brand-400">
                      {f.units.length} lakás
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

function SyntheticBuildingView({
  floors,
  floorLabel,
  onPickFloor,
}: {
  floors: { floor: number; units: ProjectUnit[] }[];
  floorLabel: (f: number) => string;
  onPickFloor: (f: number) => void;
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  return (
    <div className="lg:col-span-2 min-w-0 max-w-full">
      <p className="text-xs text-brand-500 mb-3">
        Válassza ki az emeletet — egy kattintással megnyílik az alaprajz.
      </p>
      <div className="max-w-md">
        <div
          className="mx-auto"
          style={{
            width: 0,
            height: 0,
            borderLeft: "7rem solid transparent",
            borderRight: "7rem solid transparent",
            borderBottom: "2rem solid #5a5a5a",
          }}
          aria-hidden="true"
        />
        <div className="border-x-2 border-b-2 border-[#3d3d3d] bg-[#fafafa]">
          {floors.map((f) => {
            const isHover = hovered === f.floor;
            const available = f.units.filter(
              (u) => u.status === "available",
            ).length;
            return (
              <button
                key={f.floor}
                type="button"
                onMouseEnter={() => setHovered(f.floor)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onPickFloor(f.floor)}
                className={`w-full text-left px-4 py-4 border-t-2 border-[#3d3d3d] first:border-t-0 transition-colors ${
                  isHover ? "bg-white" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-brand-800">
                    {floorLabel(f.floor).toUpperCase()}
                  </span>
                  <span className="text-[11px] text-brand-500 shrink-0">
                    {f.units.length} lakás
                    {available > 0 ? ` · ${available} szabad` : ""}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        <div className="h-1.5 bg-[#3d3d3d]" aria-hidden="true" />
      </div>
    </div>
  );
}

function SyntheticFloorPlan({
  units,
  selectedId,
  onSelect,
  pricesHidden,
}: {
  units: ProjectUnit[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  pricesHidden?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-brand-500 mb-3">
        Kattintson egy lakásra a részletekért. A színek jelzik a státuszt.
      </p>
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
        {units.map((u) => {
          const isSel = u.id === selectedId;
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => onSelect(u.id)}
              className={`text-left p-3 border-2 transition-all ${
                isSel ? "ring-2 ring-inset ring-accent" : "hover:brightness-95"
              }`}
              style={{
                background: UNIT_STATUS_FILL[u.status],
                borderColor: UNIT_STATUS_STROKE[u.status],
              }}
            >
              <div className="font-heading uppercase tracking-widish text-base text-brand-800">
                {u.label}
              </div>
              <div className="mt-1 text-xs text-brand-600">
                {u.rooms ? `${u.rooms} szoba · ` : ""}
                {formatArea(u.areaSqm as unknown as number | null)}
              </div>
              {!pricesHidden && (
                <div className="mt-1 text-sm font-semibold text-brand-800">
                  {formatPriceHuf(unitDisplayPriceHuf(u))}
                </div>
              )}
              <div
                className="mt-1 text-[10px] uppercase tracking-widish font-bold"
                style={{ color: UNIT_STATUS_STROKE[u.status] }}
              >
                {UNIT_STATUS_LABEL[u.status]}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Shared apartment details panel ─────────────────────────────────────────

function ApartmentDetailPanel({
  title,
  floorName,
  statusText,
  statusFill,
  statusStroke,
  priceHuf,
  pricesHidden,
  area,
  rooms,
  unit,
  projectSlug,
  isCompared,
  compareFull,
  onToggleCompare,
}: {
  title: string;
  floorName?: string;
  statusText: string;
  statusFill: string;
  statusStroke: string;
  priceHuf: number | null | undefined;
  pricesHidden?: boolean;
  area: number | string | null | undefined;
  rooms: number | string | null | undefined;
  unit: ProjectUnit | null;
  projectSlug: string;
  isCompared: boolean;
  compareFull: boolean;
  onToggleCompare: (unitId: string) => void;
}) {
  const compareDisabled = !isCompared && compareFull;
  return (
    <div className="rounded-lg border border-brand-200 bg-white p-5 shadow-sm">
      <div className="flex items-center flex-wrap gap-3">
        <h4 className="text-lg font-semibold text-brand-800">{title}</h4>
        <span
          className="text-xs font-medium px-2.5 py-0.5 rounded-full"
          style={{ backgroundColor: statusFill, color: statusStroke }}
        >
          {statusText}
        </span>
        {floorName && (
          <span className="text-xs text-brand-500">{floorName}</span>
        )}
      </div>

      <dl className="mt-4 grid grid-cols-3 gap-4 text-sm">
        <div>
          <dt className="text-[10px] text-brand-400 uppercase tracking-widish font-semibold">
            Ár
          </dt>
          <dd className="text-brand-800 font-semibold">
            {pricesHidden ? (
              unit ? (
                <RequestQuoteLink
                  href={unitQuoteHref(projectSlug, unit)}
                  className="text-sm"
                />
              ) : (
                <span className="text-sm text-accent font-semibold">
                  Kérjen ajánlatot
                </span>
              )
            ) : (
              formatPriceHuf(priceHuf ?? null)
            )}
          </dd>
        </div>
        <div>
          <dt className="text-[10px] text-brand-400 uppercase tracking-widish font-semibold">
            Alapterület
          </dt>
          <dd className="text-brand-800 font-semibold">{formatArea(area)}</dd>
        </div>
        <div>
          <dt className="text-[10px] text-brand-400 uppercase tracking-widish font-semibold">
            Szoba
          </dt>
          <dd className="text-brand-800 font-semibold">{rooms ?? "—"}</dd>
        </div>
      </dl>

      {unit ? (
        <div className="mt-5 flex flex-wrap gap-2">
          <Link
            href={`/projektek/${projectSlug}/${unitSlug(unit)}`}
            className="btn-primary text-xs"
          >
            <Eye size={14} />
            Lakás megtekintése
          </Link>
          <button
            type="button"
            onClick={() => onToggleCompare(unit.id)}
            disabled={compareDisabled}
            className={`text-xs ${isCompared ? "btn-primary" : "btn-secondary"} ${
              compareDisabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isCompared ? <Check size={14} /> : <Layers size={14} />}
            {isCompared ? "Kiválasztva" : "Összehasonlítás"}
          </button>
          {compareDisabled && (
            <span className="text-[11px] text-brand-400 self-center">
              Legfeljebb {MAX_COMPARE} lakás hasonlítható össze.
            </span>
          )}
        </div>
      ) : (
        <p className="mt-5 text-xs text-brand-400">
          Ehhez a zónához nincs társított lakás-adatlap, ezért az adatlap és az
          összehasonlítás nem érhető el.
        </p>
      )}
    </div>
  );
}

// ─── Floating compare bar ───────────────────────────────────────────────────

function CompareBar({
  units,
  onRemove,
  onClear,
  onStart,
}: {
  units: ProjectUnit[];
  onRemove: (unitId: string) => void;
  onClear: () => void;
  onStart: () => void;
}) {
  if (units.length === 0) return null;
  const ready = units.length >= 2;
  // Portal to <body>: an ancestor with a CSS transform (the ScrollReveal
  // wrapper on the project page) would otherwise turn position:fixed into
  // container-relative positioning, gluing the bar to the section bottom
  // instead of the viewport (QA: F-MANUAL-IBQA62UB).
  return createPortal(
    <div className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 pointer-events-none">
      <div className="mx-auto max-w-3xl pointer-events-auto rounded-xl border border-brand-200 bg-white/95 backdrop-blur shadow-xl p-3 sm:p-4">
        <div className="flex items-center flex-wrap gap-3">
          <div className="flex items-center gap-2 shrink-0 whitespace-nowrap">
            <Layers size={18} className="text-accent shrink-0" />
            <span className="text-sm font-semibold text-brand-700">
              {units.length} lakás kiválasztva
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
            {units.map((u) => (
              <span
                key={u.id}
                className="inline-flex items-center gap-1 rounded-full bg-brand-50 border border-brand-200 pl-2.5 pr-1 py-0.5 text-xs text-brand-700"
              >
                {u.label}
                <button
                  type="button"
                  onClick={() => onRemove(u.id)}
                  className="w-4 h-4 grid place-items-center text-brand-400 hover:text-accent"
                  aria-label={`${u.label} eltávolítása`}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={onClear}
              className="text-xs text-brand-500 hover:text-accent uppercase tracking-widish font-semibold"
            >
              Törlés
            </button>
            <button
              type="button"
              onClick={onStart}
              disabled={!ready}
              className={`btn-primary text-xs ${
                ready ? "" : "opacity-50 cursor-not-allowed"
              }`}
            >
              Összehasonlítás indítása
            </button>
          </div>
        </div>
        {/* Külön sorban, hogy ne folyjon össze a chipekkel (F-MANUAL-2CF0QNIP). */}
        {!ready && (
          <div className="mt-1.5 text-xs text-brand-500">
            Válasszon még legalább egy lakást az összehasonlításhoz.
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
